import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface SyncSfResult {
  ok: boolean;
  cohortSize: number;
  opportunityCount: number;
  callsScanned: number;
  callsMatched: number;
  callsSkipped: number;
  callsProcessed: number;
  callsFailed: number;
  emailsWritten: number;
  accountIds: string[];
}

/**
 * Triggers an on-demand Salesforce cohort sync (Opportunity SOQL +
 * OpportunityHistory + User snapshots + Gong call ingest in the
 * configured lookback window). Same code path as the daily scheduled
 * job; useful when you need to backfill `salesforceUsers/*` after
 * adding a new AE in Salesforce, or when the AE reminder panel is
 * complaining about owners missing from the snapshot.
 *
 * Stays in sync with the existing `SyncAllAccountsButton` UX: opens a
 * small confirm modal, surfaces a result counter inline.
 */
export function SyncSalesforceCohortButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(2);
  const [result, setResult] = useState<SyncSfResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable<{ lookbackDays: number }, SyncSfResult>(
        functions,
        'syncSalesforceNow',
      );
      const res = await fn({ lookbackDays });
      setResult(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex items-center justify-between gap-4 p-4">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-800">
          Sync Salesforce cohort
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">
          Re-runs the SF cohort SOQL, refreshes the user / opportunity-history
          snapshots, and ingests recent Gong calls. Same job as the daily
          02:00 UTC scheduler.
        </p>
        {result && (
          <p className="mt-2 text-xs text-emerald-700">
            Cohort: <span className="font-semibold">{result.cohortSize}</span>{' '}
            accounts · {result.opportunityCount} opps · scanned{' '}
            {result.callsScanned} calls (processed {result.callsProcessed},
            failed {result.callsFailed}). The "owners missing snapshot"
            warning on the reminder panel should clear after this completes.
          </p>
        )}
        {error && (
          <p className="mt-2 text-xs text-rose-700">{error}</p>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setError(null);
          setResult(null);
        }}
        disabled={busy}
        className="btn-primary whitespace-nowrap"
      >
        {busy ? 'Syncing…' : 'Sync SF cohort'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="card w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-base font-semibold text-slate-900">
              Sync Salesforce cohort now?
            </h4>
            <p className="mt-1 text-sm text-slate-600">
              Pulls the cohort opportunities, OpportunityHistory, and Owner
              user records from Salesforce, then ingests Gong calls in the
              last <span className="font-medium">{lookbackDays}</span> day
              {lookbackDays === 1 ? '' : 's'}. Safe to run repeatedly —
              ingest is idempotent.
            </p>

            <label className="mt-4 block text-sm">
              <span className="label">Gong call lookback (days)</span>
              <input
                type="number"
                min={1}
                max={90}
                value={lookbackDays}
                onChange={(e) =>
                  setLookbackDays(
                    Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 2)),
                  )
                }
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">
                The cohort + user snapshots are refreshed regardless of this
                value; this only controls how far back to re-pull Gong calls.
              </span>
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await onConfirm();
                  setOpen(false);
                }}
                disabled={busy}
                className="btn-primary"
              >
                {busy ? 'Syncing…' : 'Run sync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
